import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Card, CardContent, Grid, Alert } from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CommonPageLayout from "../../components/commonPageLayout/commonPageLayout";
import { CustomButton } from "../../components/customTable/table.styled";
import CreditCard from "./CreditCard";
import DebitCard from "./DirectDebit";
import axios from "axios";
import Loader from "../../components/loader/loader";

const CardManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCardType, setSelectedCardType] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [businessData, setBusinessData] = useState<any>(null);
  const [businessToken, setBusinessToken] = useState<string | null>(null);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);
  const [isLoadingBilling, setIsLoadingBilling] = useState<boolean>(false);

  // Get return URL from location state
  const returnUrl = location.state?.returnUrl || "/account/paymentdetails";

  const handleCardTypeSelect = (cardType: "credit" | "debit") => {
    setSelectedCardType(cardType);
    setErrorMessage(null);
  };

  const handleGoBack = () => {
    navigate(returnUrl);
  };

  // Initialize business data from localStorage
  useEffect(() => {
    const currentBusiness = localStorage.getItem("currentBusiness");
    const token = localStorage.getItem("businessAccessToken");
    if (currentBusiness) {
      setBusinessData(JSON.parse(currentBusiness));
    }
    setBusinessToken(token);
  }, []);

  // Fetch existing billing details and auto-select card type
  const fetchBillingDetails = useCallback(async () => {
    if (!businessData?.BusinessId || !businessToken) return;
    setIsLoadingBilling(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/billingplatform-business/billingdetails/${businessData.BusinessId}`,
        {
          headers: {
            Authorization: `Bearer ${businessToken}`,
            "Content-Type": "application/json"
          }
        }
      );
      const details = response.data;
      setPaymentDetails(details);
      // Auto-select based on MerchantExternalReferenceSource
      const source: string | undefined =
        details?.MerchantExternalReferenceSource;
      if (source) {
        const normalized = String(source).toLowerCase();
        if (normalized === "stripe") {
          setSelectedCardType("credit");
        } else if (normalized === "ezidebit") {
          setSelectedCardType("debit");
        } else if (normalized === "invoice") {
          setSelectedCardType(null);
        }
      }
    } catch (err) {
      // Ignore; user may not have billing details yet
    } finally {
      setIsLoadingBilling(false);
    }
  }, [businessData?.BusinessId, businessToken]);

  useEffect(() => {
    if (businessData?.BusinessId && businessToken) {
      fetchBillingDetails();
    }
  }, [businessData?.BusinessId, businessToken, fetchBillingDetails]);

  const renderCardTypeSelection = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Select Payment Method Type
      </Typography>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              cursor: "pointer",
              border:
                selectedCardType === "credit"
                  ? "2px solid #192F67"
                  : "1px solid #e0e0e0",
              "&:hover": {
                boxShadow: 3
              }
            }}
            onClick={() => handleCardTypeSelect("credit")}
          >
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              <CreditCardIcon sx={{ fontSize: 48, color: "#192F67", mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Credit Card
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add or update your credit card information securely using Stripe
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            sx={{
              cursor: "pointer",
              border:
                selectedCardType === "debit"
                  ? "2px solid #192F67"
                  : "1px solid #e0e0e0",
              "&:hover": {
                boxShadow: 3
              }
            }}
            onClick={() => handleCardTypeSelect("debit")}
          >
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              <AccountBalanceIcon
                sx={{ fontSize: 48, color: "#192F67", mb: 2 }}
              />
              <Typography variant="h6" gutterBottom>
                Direct Debit
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add or update your bank account details for direct debit
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <CommonPageLayout
      heading="Account"
      subHeading="Card Management"
      docs={
        <CustomButton
          size="small"
          variant="outlined"
          className="blue_button"
          onClick={handleGoBack}
        >
          Back
        </CustomButton>
      }
    >
      {isLoadingBilling ? (
        <Box sx={{ mt: 2, textAlign: "center", py: 4 }}>
          <Loader />
          <Typography variant="body1" color="text.secondary">
            Loading payment details...
          </Typography>
        </Box>
      ) : (
        renderCardTypeSelection()
      )}

      {!isLoadingBilling && selectedCardType === "credit" && (
        <CreditCard
          businessData={businessData}
          businessToken={businessToken}
          paymentDetails={paymentDetails}
          returnUrl={returnUrl}
        />
      )}
      {/* {!isLoadingBilling && selectedCardType === "debit" && (
        <DebitCard
          businessData={businessData}
          businessToken={businessToken}
          paymentDetails={paymentDetails}
          returnUrl={returnUrl}
        />
      )} */}
    </CommonPageLayout>
  );
};

export default CardManagement;
